/**
 * notifications.api.ts — In-app + Web Push + VK notification system.
 *
 * ┌──────────────────────────────────────────────────────────────┐
 * │  notify(userId, title, body, opts?)                           │
 * │    → EPNotification (in-app bell)  → MongoDB /v3/ep-notifications │
 * │    → Web Push (browser)            → /v3/notify/all (наш сервер)  │
 * │    → VKontakte bot                 → /v3/notify/all (наш сервер)  │
 * └──────────────────────────────────────────────────────────────┘
 *
 * Web Push и VK полностью мигрированы с Parse Cloud Functions
 * на EpWebPushService.js (наш сервер, patient-api-test).
 * Web Push подписки хранятся в MongoDB ep_push_subscriptions.
 */
import apiClient from './client';

/* ════════════════════════════════════════════════════════
   Web Push + VK через наш сервер (/v3/notify/all)
════════════════════════════════════════════════════════ */

/**
 * Отправляет Web Push и VK-уведомление через EpWebPushService на нашем сервере.
 * Fire-and-forget: ошибки не бросаются (не блокируют UI).
 */
async function sendPushAndVk(userId: string, title: string, body: string, url = '/'): Promise<void> {
  try {
    await apiClient.post('/v3/notify/all', { userId, title, body, url, sendVk: true });
  } catch {
    /* silent — Push/VK failures should not break app flow */
  }
}

/* ════════════════════════════════════════════════════════
   AppNotification (in-app bell)
════════════════════════════════════════════════════════ */
export interface AppNotification {
  id: string;
  recipientId: string;
  senderId?: string;
  type: 'graded' | 'submitted' | 'lesson' | 'material' | 'info';
  title: string;
  body: string;
  taskId?: string;
  groupId?: string;
  taskName?: string;
  groupName?: string;
  url?: string;
  read: boolean;
  createdAt: string;
}

/* ════════════════════════════════════════════════════════
   UNIFIED NOTIFY — использовать везде
════════════════════════════════════════════════════════ */

/**
 * Отправить уведомление по ВСЕМ каналам сразу:
 *  1. In-app bell (EPNotification → MongoDB /v3/ep-notifications)
 *  2. Web Push (браузерное уведомление → /v3/notify/all → EpWebPushService)
 *  3. VKontakte bot (→ /v3/notify/all → EpWebPushService → VK API)
 */
export async function notify(
  userId: string,
  title: string,
  body: string,
  opts: {
    type?: AppNotification['type'];
    taskId?: string;
    groupId?: string;
    taskName?: string;
    groupName?: string;
    url?: string;
  } = {}
): Promise<void> {
  const { type = 'info', ...rest } = opts;

  // 1. In-app bell → MongoDB
  await NotificationsAPI.create({ recipientId: userId, type, title, body, ...rest });

  // 2. Web Push + VK → наш сервер EpWebPushService (fire-and-forget)
  void sendPushAndVk(userId, title, body, rest.url || '/');
}

/* ════════════════════════════════════════════════════════
   VK LINK & STATUS
════════════════════════════════════════════════════════ */

/** Проверить статус привязки ВКонтакте */
export async function getVkStatus(userId: string): Promise<{ linked: boolean; vkUserId: string | null }> {
  try {
    const res = await apiClient.get<{ linked: boolean; vkUserId: string | null }>('/v3/vk/status', {
      params: { userId }
    });
    return res.data ?? { linked: false, vkUserId: null };
  } catch {
    return { linked: false, vkUserId: null };
  }
}

/** Сохранить VK user ID */
export async function saveVkLink(userId: string, vkUserId: string): Promise<any | null> {
  try {
    const res = await apiClient.post<{ ok: boolean; user: any }>('/v3/vk/link', { userId, vkUserId });
    if (res.data?.user) return res.data.user;
    const fallback = await apiClient.post<{ result: any }>('/user/update', { vkUserId, vkId: vkUserId });
    return fallback.data?.result ?? null;
  } catch {
    return null;
  }
}

/** Удалить VK link */
export async function removeVkLink(userId: string): Promise<any | null> {
  return saveVkLink(userId, '');
}

/* ════════════════════════════════════════════════════════
   IN-APP NOTIFICATION CRUD (MongoDB via /v3/ep-notifications)
════════════════════════════════════════════════════════ */
const NotificationsAPI = {
  /** Единая функция отправки уведомлений по всем каналам (in-app, push, vk) */
  notify,

  /** Получить последние уведомления пользователя */
  async getForUser(userId: string): Promise<AppNotification[]> {
    try {
      const res = await apiClient.get<{ notifications: AppNotification[] }>(
        '/v3/ep-notifications',
        { params: { recipientId: userId, limit: 30 } }
      );
      return res.data?.notifications ?? [];
    } catch { return []; }
  },

  /** Создать in-app уведомление */
  async create(data: Omit<AppNotification, 'id' | 'createdAt' | 'read'>): Promise<void> {
    try {
      await apiClient.post('/v3/ep-notifications', data);
    } catch { /* silent */ }
  },

  /** Отметить одно уведомление как прочитанное */
  async markRead(id: string): Promise<void> {
    try {
      await apiClient.patch(`/v3/ep-notifications/${id}/read`);
    } catch { /* silent */ }
  },

  /** Отметить все уведомления пользователя как прочитанные */
  async markAllRead(userId: string): Promise<void> {
    try {
      await apiClient.post('/v3/ep-notifications/mark-all-read', { recipientId: userId });
    } catch { /* silent */ }
  },

  /* ── Shortcuts (обратная совместимость) ── */

  /** Уведомить студента о проверенном задании */
  notifyGraded(opts: {
    studentId: string; teacherName: string; taskName: string;
    groupName: string; taskId: string; groupId: string; grade: string;
  }) {
    return notify(opts.studentId, '✅ Задание проверено', `«${opts.taskName}» — оценка ${opts.grade}`, {
      type: 'graded', taskId: opts.taskId, groupId: opts.groupId,
      taskName: opts.taskName, groupName: opts.groupName,
    });
  },

  /** Уведомить учителя о сданном задании */
  notifySubmitted(opts: {
    teacherId: string; studentName: string; taskName: string;
    groupName: string; taskId: string; groupId: string;
  }) {
    return notify(opts.teacherId, '📝 Новая сдача задания', `${opts.studentName} сдал «${opts.taskName}»`, {
      type: 'submitted', taskId: opts.taskId, groupId: opts.groupId,
      taskName: opts.taskName, groupName: opts.groupName,
    });
  },

  /** Уведомить студентов о новом занятии */
  notifyNewLesson(opts: {
    studentIds: string[]; groupName: string; groupId: string;
    lessonName: string; dateLabel: string;
  }) {
    return Promise.all(
      opts.studentIds.map((id) =>
        notify(id, '📅 Новое занятие', `${opts.groupName} — ${opts.dateLabel}${opts.lessonName ? ` · ${opts.lessonName}` : ''}`, {
          type: 'lesson', groupId: opts.groupId, groupName: opts.groupName,
          url: `/groups/${opts.groupId}`,
        })
      )
    );
  },

  /** Уведомить студентов о новом материале */
  notifyNewMaterial(opts: {
    studentIds: string[]; groupName: string; groupId: string; materialName: string;
  }) {
    return Promise.all(
      opts.studentIds.map((id) =>
        notify(id, '📚 Новый материал', `${opts.groupName}: добавлен «${opts.materialName}»`, {
          type: 'material', groupId: opts.groupId, groupName: opts.groupName,
          url: `/groups/${opts.groupId}`,
        })
      )
    );
  },
};

export default NotificationsAPI;

/* ════════════════════════════════════════════════════════
   WEB PUSH SUBSCRIPTIONS (MongoDB via /v3/push-subscriptions)
════════════════════════════════════════════════════════ */

export interface PushSubscriptionRecord {
  id?: string;
  userId: string;
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export const PushSubscriptionsAPI = {
  /** Сохранить Push-подписку браузера */
  async save(userId: string, subscription: PushSubscriptionJSON): Promise<void> {
    try {
      await apiClient.post('/v3/push-subscriptions', {
        userId,
        endpoint: subscription.endpoint,
        keys: subscription.keys,
      });
    } catch (err) {
      console.warn('[PushSubscriptionsAPI] save failed:', err);
    }
  },

  /** Удалить подписку (при отписке) */
  async delete(userId: string, endpoint: string): Promise<void> {
    try {
      await apiClient.delete('/v3/push-subscriptions', { params: { userId, endpoint } });
    } catch { /* silent */ }
  },
};

/** Тип браузерного Push-объекта */
interface PushSubscriptionJSON {
  endpoint: string;
  keys?: { p256dh?: string; auth?: string } | null;
}
